import React, { useEffect, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Phone, Mail, MapPin, CalendarDays, Loader2, Pencil, Trash2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import BookingActionButtons from "./BookingActionButtons";
import BookingEditModal from "./BookingEditModal";

const STATUS_OPTIONS = [
  "requested",
  "confirmed",
  "declined",
  "completed",
  "cancelled",
];

const STATUS_STYLES = {
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  declined: "bg-rose-100 text-rose-800",
  completed: "bg-slate-200 text-slate-700",
  cancelled: "bg-slate-200 text-slate-500 line-through",
};

export default function AdminBookings() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/bookings");
      setBookings(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      const r = await authAxios.patch(`/bookings/${id}/status`, { status });
      setBookings((arr) => arr.map((b) => (b.id === id ? r.data : b)));
      if (selected?.id === id) setSelected(r.data);
      toast({ title: "Status updated" });
    } catch (e) {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  const handleActionCompleted = (updated) => {
    setBookings((arr) => arr.map((b) => (b.id === updated.id ? updated : b)));
    if (selected?.id === updated.id) setSelected(updated);
  };

  const handleBookingDeleted = (id) => {
    setBookings((arr) => arr.filter((b) => b.id !== id));
    if (selected?.id === id) setSelected(null);
    setCheckedIds((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  };

  const toggleChecked = (id) => {
    setCheckedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const filtered = bookings.filter((b) => filter === "all" || b.status === filter);
  const allFilteredChecked = filtered.length > 0 && filtered.every((b) => checkedIds.has(b.id));
  const someFilteredChecked = filtered.some((b) => checkedIds.has(b.id));

  const toggleAll = () => {
    setCheckedIds((s) => {
      const n = new Set(s);
      if (allFilteredChecked) {
        filtered.forEach((b) => n.delete(b.id));
      } else {
        filtered.forEach((b) => n.add(b.id));
      }
      return n;
    });
  };

  const clearSelection = () => setCheckedIds(new Set());

  const doBulkDelete = async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const r = await authAxios.post("/bookings/bulk-delete", { ids });
      const deletedCount = r.data?.deleted ?? ids.length;
      setBookings((arr) => arr.filter((b) => !checkedIds.has(b.id)));
      if (selected && checkedIds.has(selected.id)) setSelected(null);
      clearSelection();
      setBulkDeleteOpen(false);
      toast({ title: `Deleted ${deletedCount} booking${deletedCount === 1 ? "" : "s"}` });
    } catch (e) {
      toast({
        title: "Bulk delete failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-extrabold text-slate-900">Bookings</h1>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}>Refresh</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_460px] gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer select-none">
                <Checkbox
                  checked={allFilteredChecked}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-bookings"
                  aria-label="Select all"
                />
                {checkedIds.size > 0 ? (
                  <span className="text-sky-700">{checkedIds.size} selected</span>
                ) : (
                  <span>{filtered.length} bookings</span>
                )}
              </label>
              {checkedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={() => setBulkDeleteOpen(true)}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete {checkedIds.size}
                  </Button>
                </div>
              )}
            </div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {filtered.map((b) => {
                const isChecked = checkedIds.has(b.id);
                return (
                  <div
                    key={b.id}
                    className={`flex items-stretch hover:bg-slate-50 transition-colors ${
                      selected?.id === b.id ? "bg-sky-50" : ""
                    } ${isChecked ? "bg-rose-50/40" : ""}`}
                  >
                    <label
                      className="flex items-center pl-4 pr-2 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleChecked(b.id)}
                        data-testid={`select-booking-${b.id}`}
                        aria-label={`Select ${b.name}`}
                      />
                    </label>
                    <button
                      onClick={() => setSelected(b)}
                      className="flex-1 text-left pl-1 pr-5 py-4"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-slate-800">{b.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[b.status] || "bg-slate-100 text-slate-700"}`}>{b.status}</span>
                      </div>
                      <div className="text-xs text-slate-500">{b.reference} • {b.service_title}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {b.date} at {b.time}{b.time_to ? ` - ${b.time_to}` : ""} • {b.suburb}
                      </div>
                    </button>
                  </div>
                );
              })}
              {filtered.length === 0 && <div className="px-5 py-8 text-sm text-slate-500">No bookings match this filter.</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 h-fit">
            {!selected ? (
              <div className="text-sm text-slate-500 py-10 text-center">Select a booking to view details.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{selected.reference}</div>
                    <div className="font-extrabold text-lg text-slate-900">{selected.name}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLES[selected.status] || "bg-slate-100 text-slate-700"}`}>
                    {selected.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <BookingActionButtons booking={selected} onUpdated={handleActionCompleted} />
                  <Button
                    onClick={() => setEditOpen(true)}
                    variant="outline"
                    size="sm"
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="edit-booking-btn"
                  >
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Info icon={Phone} label="Phone" value={selected.phone} />
                <Info icon={Mail} label="Email" value={selected.email || "—"} />
                <Info icon={MapPin} label="Address" value={`${selected.address}, ${selected.suburb}`} />
                <Info icon={CalendarDays} label="Appointment" value={`${selected.date} at ${selected.time}${selected.time_to ? ` - ${selected.time_to}` : ""}`} />
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Service</div>
                  <div className="text-sm text-slate-800">{selected.service_title}</div>
                </div>
                {selected.notes && (
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Notes</div>
                    <div className="text-sm text-slate-700 bg-slate-50 rounded p-3">{selected.notes}</div>
                  </div>
                )}
                {selected.image_urls?.length > 0 && (
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Attached photos</div>
                    <div className="grid grid-cols-3 gap-2">
                      {selected.image_urls.map((u) => (
                        <a key={u} href={u} target="_blank" rel="noreferrer" className="block aspect-square rounded overflow-hidden border">
                          <img src={u} alt="attachment" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs pt-3 border-t mt-3">
                  <Badge ok={selected.sms_sent} label="Customer SMS" />
                  <Badge ok={selected.business_sms_sent} label="Business SMS" />
                  <Badge ok={selected.email_sent} label="Customer Email" />
                  <Badge ok={selected.business_email_sent} label="Business Email" />
                </div>
                {selected.customer_reply && (
                  <div className="text-xs text-slate-600 bg-sky-50 rounded p-2">
                    Customer replied <strong>{selected.customer_reply}</strong> at {selected.customer_reply_at?.slice(0, 16)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <BookingEditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        booking={selected}
        onSaved={handleActionCompleted}
        onDeleted={handleBookingDeleted}
      />

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {checkedIds.size} booking{checkedIds.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected booking records. Customers are NOT notified. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={bulkDeleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-bulk-delete-btn"
            >
              {bulkDeleting && <Loader2 className="animate-spin mr-2" size={14} />}
              Delete {checkedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-sky-600 mt-0.5" />
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm font-medium text-slate-800 break-all">{value}</div>
      </div>
    </div>
  );
}

function Badge({ ok, label }) {
  return (
    <div className={`flex items-center gap-1 ${ok ? "text-sky-700" : "text-slate-500"}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-sky-500" : "bg-slate-300"}`} />
      {label}
    </div>
  );
}
